import React, { useEffect, useState } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const MyProfile = ({ onNavigate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('notifications'); 
    
    // Form State
    const [prefs, setPrefs] = useState({});
    const [password, setPassword] = useState('');

    // Notifications State
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        loadData();
    }, [activeTab]);

    const loadData = async () => {
        try {
            const res = await api.get('my-profile/');
            setData(res.data);
            setPrefs(res.data.preferences);

            if (activeTab === 'notifications') {
                const notifRes = await api.get('notifications/');
                // Only load UNREAD notifications if you want them to disappear after reading
                // Or load all and filter in render. Here we load all but filter below.
                setNotifications(notifRes.data);
            }
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        try {
            const payload = { ...prefs };
            if (password) payload.new_password = password;
            await api.patch('my-profile/', payload);
            alert("✅ Profile Updated!");
            setPassword('');
        } catch (err) { alert("Update failed"); }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.post('notifications/read/'); // Call the fixed backend endpoint
            
            // 1. Clear local list instantly (Optimistic UI)
            setNotifications([]); 

            // 2. Emit custom event to tell SaaSLayout to refresh the bell
            window.dispatchEvent(new Event('notificationsRead'));

        } catch(err) { console.error("Failed to mark read"); }
    };

    const togglePref = (key) => {
        setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleLinkClick = (link) => {
        if (!onNavigate || !link) return;

        if (link.includes('/permits')) {
            onNavigate('permits');
        } else if (link.includes('/logs')) {
            onNavigate('overview');
        } else if (link.includes('/actions')) {
            onNavigate('actions'); 
        } else if (link.includes('/equipment')) {
            onNavigate('equipment');
        } else if (link.includes('/audit')) {
            onNavigate('audits');
        } else {
            alert("Unable to open this link directly.");
        }
    };

    const timeAgo = (dateString) => {
        const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + " years ago";
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + " months ago";
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + " days ago";
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + " hours ago";
        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + " minutes ago";
        return "Just now";
    };

    if (loading && !data) return <div style={{color:'white', padding:'20px'}}>Loading Profile...</div>;

    const LabelSwitch = ({ label, id }) => (
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'15px', borderBottom:`1px solid ${THEME.border}`}}>
            <span style={{color:'white'}}>{label}</span>
            <div 
                onClick={() => togglePref(id)}
                style={{
                    width:'40px', height:'20px', borderRadius:'20px', 
                    background: prefs[id] ? THEME.success : '#334155', 
                    position:'relative', cursor:'pointer', transition:'0.2s'
                }}
            >
                <div style={{
                    width:'16px', height:'16px', borderRadius:'50%', background:'white', 
                    position:'absolute', top:'2px', 
                    left: prefs[id] ? '22px' : '2px', transition:'0.2s'
                }} />
            </div>
        </div>
    );

    // Filter logic: Only show UNREAD notifications in the list
    const visibleNotifications = notifications.filter(n => !n.is_read);

    return (
        <div className="animate-fade-in" style={{padding:'20px', maxWidth:'800px', margin:'0 auto'}}>
            
            {/* HEADER */}
            <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'20px', display:'flex', alignItems:'center', gap:'20px'}}>
                <div style={{width:'80px', height:'80px', borderRadius:'50%', background:THEME.primary, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2rem', color:'white'}}>
                    {data?.user?.username[0].toUpperCase()}
                </div>
                <div>
                    <h2 style={{margin:0, color:'white'}}>{data?.user?.username}</h2>
                    <div style={{color:THEME.textSec}}>{data?.user?.role} @ {data?.user?.company}</div>
                    <div style={{marginTop:'5px', color: data?.user?.is_inducted ? THEME.success : THEME.danger, fontWeight:'bold', fontSize:'0.9rem'}}>
                        {data?.user?.is_inducted ? "✅ Inducted" : "❌ Not Inducted"}
                    </div>
                </div>
            </div>

            {/* TABS */}
            <div style={{display: 'flex', gap: '10px', marginBottom: '20px', overflowX:'auto'}}>
                <button onClick={() => setActiveTab('notifications')} style={{padding:'10px 20px', borderRadius:'6px', border:'none', background: activeTab==='notifications' ? THEME.primary : THEME.card, color:'white', cursor:'pointer', fontWeight:'bold'}}>🔔 Notifications</button>
                <button onClick={() => setActiveTab('settings')} style={{padding:'10px 20px', borderRadius:'6px', border:'none', background: activeTab==='settings' ? THEME.primary : THEME.card, color:'white', cursor:'pointer'}}>Settings & Password</button>
                <button onClick={() => setActiveTab('quals')} style={{padding:'10px 20px', borderRadius:'6px', border:'none', background: activeTab==='quals' ? THEME.primary : THEME.card, color:'white', cursor:'pointer'}}>My Qualifications</button>
            </div>

            {/* --- TAB 1: NOTIFICATIONS --- */}
            {activeTab === 'notifications' && (
                <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, padding:'20px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                        <h3 style={{color:'white', margin:0}}>Unread Notifications</h3>
                        {visibleNotifications.length > 0 && (
                            <button onClick={handleMarkAllRead} style={{background:'transparent', border:'none', color:THEME.primary, cursor:'pointer', fontSize:'0.9rem', fontWeight:'bold'}}>
                                ✓ Mark all as read
                            </button>
                        )}
                    </div>

                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        {visibleNotifications.length === 0 && (
                            <div style={{color:THEME.textSec, fontStyle:'italic', textAlign:'center', padding:'30px', border:`1px dashed ${THEME.border}`, borderRadius:'8px'}}>
                                You're all caught up! No new notifications.
                            </div>
                        )}
                        
                        {visibleNotifications.map(n => (
                            <div key={n.id} style={{
                                padding:'15px', borderRadius:'8px', 
                                background: '#1e293b', 
                                borderLeft: `4px solid ${THEME.primary}`, 
                                borderBottom: `1px solid ${THEME.border}`,
                                display:'flex', gap:'15px', alignItems:'start'
                            }}>
                                <div style={{fontSize:'1.2rem'}}>
                                    {n.type === 'DANGER' ? '🚨' : n.type === 'WARNING' ? '⚠️' : 'ℹ️'}
                                </div>
                                <div style={{flex:1}}>
                                    <div style={{display:'flex', justifyContent:'space-between'}}>
                                        <div style={{fontWeight:'bold', color: 'white'}}>{n.title}</div>
                                        <div style={{fontSize:'0.75rem', color:THEME.textSec}}>{timeAgo(n.created_at)}</div>
                                    </div>
                                    <div style={{color: THEME.textSec, fontSize:'0.9rem', marginTop:'5px'}}>{n.message}</div>
                                    
                                    {n.link && (
                                        <div style={{marginTop:'10px'}}>
                                            <button 
                                                onClick={() => handleLinkClick(n.link)}
                                                style={{
                                                    background: 'transparent', border: 'none', padding: 0,
                                                    color: THEME.primary, fontSize: '0.85rem', 
                                                    fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline'
                                                }}
                                            >
                                                View Details →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* --- TAB 2: SETTINGS --- */}
            {activeTab === 'settings' && (
                <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, padding:'20px'}}>
                    <h3 style={{color:'white', marginTop:0, borderBottom:`1px solid ${THEME.border}`, paddingBottom:'10px'}}>🔔 Notification Preferences</h3>
                    
                    {/* Global Master Switch */}
                    <LabelSwitch label="📧 Receive Email Notifications" id="email_notifications" />
                    
                    <div style={{marginTop:'20px', marginBottom:'10px', color:THEME.primary, fontSize:'0.85rem', fontWeight:'bold', textTransform:'uppercase', letterSpacing:'1px'}}>
                        In-App Alerts
                    </div>

                    {/* Granular Switches */}
                    <LabelSwitch label="📋 New Daily Logs" id="notify_new_log" />
                    <LabelSwitch label="✍️ My Log Updates" id="notify_my_log" />
                    <LabelSwitch label="🦺 New Permit Requests" id="notify_new_permit" />
                    <LabelSwitch label="🚨 Safety Incidents" id="notify_new_incident" />
                    <LabelSwitch label="⚡ Actions Assigned to Me" id="notify_action_assigned" />
                    <LabelSwitch label="🚜 Equipment Expiry" id="notify_equipment_due" />
                    <LabelSwitch label="📄 Document Expiry/Updates" id="notify_doc_submitted" />

                    <div style={{marginTop:'30px', paddingTop:'20px', borderTop:`1px solid ${THEME.border}`}}>
                        <h3 style={{color:'white', marginTop:0}}>🔒 Security</h3>
                        <label style={{display:'block', color:THEME.textSec, fontSize:'0.9rem', marginBottom:'8px'}}>Change Password</label>
                        <input 
                            type="password" 
                            placeholder="Enter new password..." 
                            value={password} 
                            onChange={e => setPassword(e.target.value)} 
                            style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', boxSizing:'border-box'}}
                        />
                    </div>

                    <div style={{marginTop:'25px', textAlign:'right'}}>
                        <button onClick={handleSave} style={{padding:'12px 30px', background:THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Save Changes</button>
                    </div>
                </div>
            )}

            {/* --- TAB 3: QUALIFICATIONS --- */}
            {activeTab === 'quals' && (
                <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, padding:'20px'}}>
                    {/* ... content same as before ... */}
                </div>
            )}
        </div>
    );
};

export default MyProfile;